import React, { useState } from "react";
import styled from "styled-components";
import { useAmazonContext } from "../../Contexts/AmazonContext";
import api from "../api";
import BottomNavBar from "./BottomNavBar";

const Container = styled.div`
  max-width: 700px;
  margin: 0 auto;
  padding: 24px 20px 90px 20px;
  min-height: 100vh;
  background: ${(props) => (props.isDarkMode ? "#262c3c" : "#FCF5E5")};
`;

const Card = styled.div`
  background: ${(props) => (props.isDarkMode ? "#1f1f1f" : "#fff")};
  border-radius: 14px;
  padding: 24px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid ${(props) => (props.isDarkMode ? "#2f2f2f" : "#eee")};
`;

const Title = styled.h1`
  margin: 0 0 16px 0;
  font-size: 24px;
  color: ${(props) => (props.isDarkMode ? "#fff" : "#333")};
  font-family: "Quicksand", sans-serif;
`;

const Description = styled.p`
  margin: 0 0 20px 0;
  font-size: 14px;
  color: ${(props) => (props.isDarkMode ? "#cfcfcf" : "#555")};
  font-family: "Quicksand", sans-serif;
  line-height: 1.5;
`;

const FormRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 18px;
`;

const Label = styled.div`
  font-size: 13px;
  color: ${(props) => (props.isDarkMode ? "#e0e0e0" : "#444")};
  font-family: "Quicksand", sans-serif;
  font-weight: 600;
`;

const Input = styled.input`
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid ${(props) => (props.isDarkMode ? "#333" : "#ddd")};
  background: ${(props) => (props.isDarkMode ? "#1b1b1b" : "#fff")};
  color: ${(props) => (props.isDarkMode ? "#f5f5f5" : "#333")};
  font-size: 14px;
  font-family: "Quicksand", sans-serif;

  &:focus {
    outline: 2px solid #8747d4;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  flex: 1;
  min-width: 140px;
  padding: 12px 16px;
  border: none;
  border-radius: 10px;
  font-weight: 700;
  font-size: 14px;
  font-family: "Quicksand", sans-serif;
  cursor: pointer;
  color: #fff;
  box-shadow: 0 4px 0 rgba(0, 0, 0, 0.12);
  transition: transform 0.05s ease, box-shadow 0.05s ease, opacity 0.2s ease;

  &:active {
    transform: translateY(1px);
    box-shadow: 0 2px 0 rgba(0, 0, 0, 0.1);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const FreezeButton = styled(ActionButton)`
  background: #8747d4;
  box-shadow: 0 4px 0 #6535a3;
  &:hover:not(:disabled) {
    background: #9859e0;
  }
`;

const UnfreezeButton = styled(ActionButton)`
  background: #d14343;
  box-shadow: 0 4px 0 #a23232;
  &:hover:not(:disabled) {
    background: #e35a5a;
  }
`;

const Status = styled.div`
  margin-top: 14px;
  font-size: 13px;
  font-family: "Quicksand", sans-serif;
  color: ${(props) => {
    if (props.type === "error") return "#d14343";
    if (props.type === "success") return "#1b9a59";
    return props.isDarkMode ? "#cfcfcf" : "#555";
  }};
`;

const StreakFreezePage = () => {
  const { isDarkMode } = useAmazonContext();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState({ type: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (freeze) => {
    if (!email.trim()) {
      setStatus({ type: "error", message: "Please enter an email." });
      return;
    }
    setLoading(true);
    setStatus({ type: "", message: "" });
    try {
      const res = await api.post("/api/admin/freeze-streak", {
        userEmail: email.trim(),
        freeze,
      });
      setStatus({
        type: "success",
        message: res.data?.message || `Streak ${freeze ? "frozen" : "unfrozen"}.`,
      });
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to update streak.";
      setStatus({ type: "error", message: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Container isDarkMode={isDarkMode}>
        <Card isDarkMode={isDarkMode}>
          <Title isDarkMode={isDarkMode}>Streak Freeze</Title>
          <Description isDarkMode={isDarkMode}>
            Lock or unlock a user’s streak. When frozen, their streak will not
            increment or reset.
          </Description>

          <FormRow>
            <Label isDarkMode={isDarkMode}>User email</Label>
            <Input
              isDarkMode={isDarkMode}
              type="email"
              placeholder="user@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
            />
          </FormRow>

          <ButtonRow>
            <FreezeButton onClick={() => submit(true)} disabled={loading}>
              {loading ? "Working..." : "Freeze Streak"}
            </FreezeButton>
            <UnfreezeButton onClick={() => submit(false)} disabled={loading}>
              {loading ? "Working..." : "Unfreeze Streak"}
            </UnfreezeButton>
          </ButtonRow>

          {status.message && (
            <Status type={status.type} isDarkMode={isDarkMode}>
              {status.message}
            </Status>
          )}
        </Card>
      </Container>
      <BottomNavBar />
    </>
  );
};

export default StreakFreezePage;

